"use client";

import { Button } from "@heroui/button";
import { Tooltip } from "@heroui/tooltip";
import { PlayIcon, ZapIcon } from "@icons";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useWorkflowSelection } from "@/features/chat/hooks/useWorkflowSelection";
import { useIntegrations } from "@/features/integrations/hooks/useIntegrations";
import { Link } from "@/i18n/navigation";
import { ANALYTICS_EVENTS, trackEvent } from "@/lib/analytics";
import { toast } from "@/lib/toast";
import { useAppendToInput } from "@/stores/composerStore";
import type {
  CommunityWorkflow,
  IntegrationRef,
  PublicWorkflowStep,
  Workflow,
} from "@/types/features/workflowTypes";
import { formatRunCount } from "@/utils/formatters";
import { useWorkflowCreation } from "../../hooks/useWorkflowCreation";
import { getTriggerDisplayInfo } from "../../triggers/utils";
import {
  ActivationStatus,
  CreatorAvatar,
  getNextRunDisplay,
  MissingIntegrationsWarning,
  SystemWorkflowChip,
  TriggerDisplay,
} from "./WorkflowCardComponents";
import WorkflowIcons from "./WorkflowIcons";

type WorkflowVariant = "user" | "community" | "explore" | "suggestion";
type ActionType = "run" | "create" | "insert-prompt" | "navigate" | "none";

interface UnifiedWorkflowCardProps {
  // Core data - accepts either full Workflow or simplified data
  workflow?: Workflow;
  communityWorkflow?: CommunityWorkflow;
  // Simplified props for direct use (alternative to workflow objects)
  title?: string;
  description?: string;
  steps?: PublicWorkflowStep[];
  totalExecutions?: number;
  slug?: string;
  prompt?: string;
  actionType?: "prompt" | "workflow";

  // Display configuration
  variant?: WorkflowVariant;
  showTrigger?: boolean;
  showExecutions?: boolean;
  showActivationStatus?: boolean;
  showCreator?: boolean;
  useBlurEffect?: boolean;
  showDescriptionAsTooltip?: boolean;

  // Action configuration
  primaryAction?: ActionType;
  onCardClick?: () => void;
  onActionComplete?: () => void;
  /**
   * When set, the whole card becomes a real crawlable link to this URL (no
   * client-side router.push). The action button stays clickable above it.
   */
  href?: string;

  // Button customization
  actionButtonLabel?: string;

  // Explicit missing integrations override (for cards that don't pass a full workflow object)
  missingIntegrations?: IntegrationRef[];
}

export default function UnifiedWorkflowCard(props: UnifiedWorkflowCardProps) {
  const {
    workflow,
    communityWorkflow,
    description: propDescription,
    slug,
    prompt,
    actionType: propActionType,
    variant = "explore",
    showExecutions = true,
    useBlurEffect = false,
    showDescriptionAsTooltip = false,
    onCardClick,
    onActionComplete,
    actionButtonLabel,
    href,
  } = props;
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  // Auth check
  const { isAuthenticated, openLoginModal } = useAuth();

  const { selectWorkflow } = useWorkflowSelection();
  const { createWorkflow } = useWorkflowCreation();
  const { integrations } = useIntegrations();
  const appendToInput = useAppendToInput();

  // Normalize data + display settings from the various supported prop sources.
  const {
    title,
    displayDescription,
    steps,
    totalExecutions,
    creator,
    shouldShowTrigger,
    shouldShowCreator,
    shouldShowActivation,
    resolvedAction,
    resolvedMissingIntegrations,
    isClickable,
  } = deriveWorkflowCardConfig(props);

  // Get trigger info for user workflows
  const triggerDisplay = workflow
    ? getTriggerDisplayInfo(workflow, integrations)
    : null;
  const nextRunText = workflow ? getNextRunDisplay(workflow) : null;

  // Action handlers
  const handleRunWorkflow = async () => {
    if (!workflow || isLoading) return;
    setIsLoading(true);
    try {
      selectWorkflow(workflow, { autoSend: true });
      onActionComplete?.();
    } catch (error) {
      console.error("Error running workflow:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateWorkflow = async () => {
    if (isLoading) return;

    // Check authentication first - open login modal if not authenticated
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }

    setIsLoading(true);
    const toastId = toast.loading("Creating workflow...");

    try {
      // Convert PublicWorkflowStep to WorkflowStepData format if steps exist
      const formattedSteps = steps?.map((step, index) => ({
        id: step.id || `step_${index}`,
        title: step.title,
        description: step.description,
        category: step.category,
      }));

      const workflowRequest = {
        title,
        description:
          communityWorkflow?.description || propDescription || undefined,
        prompt: communityWorkflow?.prompt || displayDescription || title,
        trigger_config: {
          type: "manual" as const,
          enabled: true,
        },
        // Pass formatted steps if available to avoid regeneration
        ...(formattedSteps &&
          formattedSteps.length > 0 && {
            steps: formattedSteps,
          }),
        // Only generate if no steps exist
        generate_immediately: !formattedSteps || formattedSteps.length === 0,
      };

      const result = await createWorkflow(workflowRequest);

      if (result.success && result.workflow) {
        toast.success("Workflow created successfully!", { id: toastId });
        selectWorkflow(result.workflow, { autoSend: variant === "suggestion" });
        onActionComplete?.();
      }
    } catch (error) {
      toast.error("Error creating workflow", { id: toastId });
      console.error("Workflow creation error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInsertPrompt = () => {
    if (prompt) {
      trackEvent(ANALYTICS_EVENTS.USE_CASES_PROMPT_INSERTED, { title });
      appendToInput(prompt);
      router.push("/c");
      onActionComplete?.();
    }
  };

  const handleNavigate = () => {
    const targetSlug = slug || communityWorkflow?.slug || workflow?.slug;
    if (targetSlug) {
      trackEvent(ANALYTICS_EVENTS.WORKFLOW_CARD_NAVIGATE, {
        slug: targetSlug,
        variant,
      });
      router.push(`/use-cases/${targetSlug}`);
    }
  };

  const handlePrimaryAction = async () => {
    switch (resolvedAction) {
      case "run":
        await handleRunWorkflow();
        break;
      case "create":
        await handleCreateWorkflow();
        break;
      case "insert-prompt":
        handleInsertPrompt();
        break;
      case "navigate":
        handleNavigate();
        break;
      default:
        break;
    }
  };

  const handleCardClick = () => {
    if (onCardClick) {
      onCardClick();
      return;
    }

    // Default card click behavior
    if (variant === "suggestion") {
      handleCreateWorkflow();
    } else if (variant === "user" && workflow) {
      handleRunWorkflow();
    } else {
      handleNavigate();
    }
  };

  // Get button configuration
  const buttonConfig = getButtonConfig(
    resolvedAction,
    actionButtonLabel,
    propActionType,
  );

  // Render tool icons using the shared component
  const renderToolIcons = () => (
    <WorkflowIcons steps={steps} iconSize={25} maxIcons={3} />
  );

  const cardContent = (
    <div
      className={`group relative z-1 flex h-full min-h-fit w-full flex-col gap-2 rounded-3xl outline-1 ${useBlurEffect ? "bg-zinc-800/40 outline-zinc-800/50 backdrop-blur-lg" : "bg-zinc-800 outline-zinc-800/70"} p-4 transition-all select-none ${isClickable ? "cursor-pointer hover:bg-zinc-700/50" : ""}`}
      onClick={href ? undefined : handleCardClick}
    >
      {href && (
        <Link
          href={href}
          aria-label={title}
          className="absolute inset-0 z-[1] rounded-3xl"
        />
      )}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">{renderToolIcons()}</div>
        <div className="flex items-center gap-2">
          {resolvedMissingIntegrations?.length ? (
            // The warning replaces the activation badge: a workflow with
            // unconnected integrations can't run, so its activated/deactivated
            // state is moot until they're connected.
            <MissingIntegrationsWarning
              missingIntegrations={resolvedMissingIntegrations}
            />
          ) : (
            shouldShowActivation &&
            workflow && <ActivationStatus activated={workflow.activated} />
          )}
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2">
          <h3 className="line-clamp-2 text-lg font-medium">{title}</h3>
        </div>
        {!showDescriptionAsTooltip && (
          <div className="mt-1 line-clamp-2 min-h-8 flex-1 text-xs text-zinc-500">
            {displayDescription}
          </div>
        )}
      </div>

      <div className="mt-auto">
        <div className="mt-1 flex items-center justify-between gap-2">
          <div className="space-y-1">
            {shouldShowTrigger && triggerDisplay && (
              <TriggerDisplay
                triggerType={workflow?.trigger_config.type || "manual"}
                triggerLabel={triggerDisplay.label
                  .split(" ")
                  .map(
                    (word: string) =>
                      word.charAt(0).toUpperCase() +
                      word.slice(1).toLowerCase(),
                  )
                  .join(" ")}
                integrationId={triggerDisplay.integrationId}
                nextRunText={nextRunText || undefined}
              />
            )}

            {showExecutions && totalExecutions > 0 && (
              <div className="flex items-center gap-1 text-xs text-zinc-500">
                <PlayIcon
                  width={15}
                  height={15}
                  className="w-4 text-zinc-500"
                />
                <span className="text-nowrap">
                  {formatRunCount(totalExecutions)}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            {workflow?.is_system_workflow && <SystemWorkflowChip />}
            {shouldShowCreator && creator && (
              <CreatorAvatar creator={creator} />
            )}

            {resolvedAction !== "none" && (
              <span className="relative z-[2]">
                <WorkflowActionButton
                  label={buttonConfig.label}
                  isLoading={isLoading}
                  onPress={handlePrimaryAction}
                  variant={buttonConfig.variant}
                />
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return showDescriptionAsTooltip ? (
    <Tooltip
      content={workflow?.prompt || displayDescription}
      placement="top"
      className="max-w-xs"
      showArrow
      classNames={{
        content: "bg-zinc-800 p-4 rounded-3xl",
      }}
      delay={200}
      closeDelay={0}
    >
      {cardContent}
    </Tooltip>
  ) : (
    cardContent
  );
}

// Resolve the card's display data and settings from the various supported prop
// shapes (full Workflow, CommunityWorkflow, or simplified direct props).
function deriveWorkflowCardConfig(props: UnifiedWorkflowCardProps) {
  const {
    workflow,
    communityWorkflow,
    title: propTitle,
    description: propDescription,
    steps: propSteps,
    totalExecutions: propTotalExecutions,
    variant = "explore",
    showTrigger,
    showActivationStatus = false,
    showCreator,
    primaryAction,
    onCardClick,
    href,
    missingIntegrations: propMissingIntegrations,
  } = props;

  const title = propTitle || workflow?.title || communityWorkflow?.title || "";
  const displayDescription =
    propDescription ||
    workflow?.description ||
    communityWorkflow?.description ||
    "";
  const steps = propSteps || workflow?.steps || communityWorkflow?.steps || [];
  const totalExecutions =
    propTotalExecutions ??
    workflow?.total_executions ??
    communityWorkflow?.total_executions ??
    0;
  const creator = communityWorkflow?.creator || workflow?.creator;

  const shouldShowTrigger = showTrigger ?? (variant === "user" && !!workflow);
  const shouldShowCreator =
    showCreator ?? (variant === "community" && !!creator);
  const shouldShowActivation =
    showActivationStatus ?? (variant === "user" && !!workflow);

  const resolvedAction = primaryAction ?? getDefaultAction(variant);
  const resolvedMissingIntegrations =
    propMissingIntegrations ?? workflow?.missing_integrations;
  const isClickable = !!href || onCardClick || resolvedAction !== "none";

  return {
    title,
    displayDescription,
    steps,
    totalExecutions,
    creator,
    shouldShowTrigger,
    shouldShowCreator,
    shouldShowActivation,
    resolvedAction,
    resolvedMissingIntegrations,
    isClickable,
  };
}

// Helper function to determine default action based on variant
function getDefaultAction(variant: WorkflowVariant): ActionType {
  switch (variant) {
    case "user":
      return "run";
    case "community":
    case "explore":
      return "create";
    case "suggestion":
      return "create";
    default:
      return "none";
  }
}

// Helper function to get button configuration
function getButtonConfig(
  action: ActionType,
  customLabel?: string,
  propActionType?: "prompt" | "workflow",
): { label: string; variant: "primary" | "flat" } {
  if (customLabel) {
    return { label: customLabel, variant: "primary" };
  }

  // Handle prompt vs workflow action type
  if (propActionType === "prompt") {
    return { label: "Insert Prompt", variant: "primary" };
  }

  switch (action) {
    case "run":
      return { label: "Run", variant: "flat" };
    case "create":
      return { label: "Create", variant: "primary" };
    case "insert-prompt":
      return { label: "Insert Prompt", variant: "primary" };
    case "navigate":
      return { label: "View", variant: "flat" };
    default:
      return { label: "Try", variant: "primary" };
  }
}

// Unified action button component
interface WorkflowActionButtonProps {
  label: string;
  isLoading: boolean;
  onPress: (e: React.MouseEvent) => void;
  variant?: "primary" | "flat";
  size?: "sm" | "md";
}

function WorkflowActionButton({
  label,
  isLoading,
  onPress,
  variant = "primary",
  size = "sm",
}: WorkflowActionButtonProps) {
  const buttonVariant = variant === "flat" ? "flat" : "solid";
  const showIcon = label !== "Run Workflow";

  return (
    <Button
      color="primary"
      size={size}
      variant={buttonVariant}
      className={`font-medium rounded-xl ${variant === "flat" ? "text-primary" : ""}`}
      isLoading={isLoading}
      onPress={(e) => onPress(e as unknown as React.MouseEvent)}
      endContent={showIcon ? <ZapIcon width={16} height={16} /> : undefined}
    >
      {label}
    </Button>
  );
}
